import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, FileText, User } from "lucide-react";
import { API } from "../../context/AuthContext";
import RevealPayout from "./RevealPayout";

const eur = (n) => `€${Number(n || 0).toFixed(2)}`;

export function groupExpenses(expenses) {
  const groups = {};
  for (const e of expenses) {
    const g = (groups[e.payout_group || e.id] ||= { key: e.payout_group || e.id, items: [], total: 0 });
    g.items.push(e);
    g.total += e.amount || 0;
  }
  return Object.values(groups).sort((a, b) => b.total - a.total);
}

const openAttachment = async (id) => {
  try {
    const r = await fetch(`${API}/expenses/${id}/attachment`, { credentials: "include" });
    if (!r.ok) throw new Error();
    window.open(URL.createObjectURL(await r.blob()), "_blank");
  } catch { toast.error("Failed to load attachment"); }
};

function Line({ e, onPay }) {
  return (
    <div className="px-3 py-2 flex items-center gap-2 text-xs border-t" data-testid={`settle-line-${e.id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-700 truncate">{e.description}</p>
        <p className="text-slate-500 truncate">
          {e.event_name}{e.invoice_number ? ` · Inv ${e.invoice_number}` : ""} · approved {e.approved_at ? new Date(e.approved_at).toLocaleDateString() : "—"}
        </p>
        {e.attachment_name && (
          <button onClick={() => openAttachment(e.id)} className="flex items-center gap-1 text-blue-600 hover:underline mt-0.5"><FileText className="w-3 h-3" /> {e.attachment_name}</button>
        )}
      </div>
      <span className="font-bold text-[#1e3a5f]">{eur(e.amount)}</span>
      <button onClick={() => onPay([e])} className="px-2 py-1 rounded-lg border border-green-500 text-green-700 font-bold whitespace-nowrap hover:bg-green-50" data-testid={`pay-${e.id}`}>Mark paid</button>
    </div>
  );
}

export default function ExpenseSettlementGroup({ group, onPay }) {
  const [open, setOpen] = useState(false);
  const head = group.items[0];
  const method = head.payout_method || "iban";
  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid={`settle-group-${group.key}`}>
      <div className="p-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0"><User className="w-4 h-4" /></div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-[#1e3a5f] truncate">{head.submitter_name}</p>
          <p className="text-xs text-slate-500 truncate">{head.submitter_email} · {group.items.length} expense{group.items.length > 1 ? "s" : ""}</p>
          <div className="text-xs text-slate-600 mt-0.5" data-testid={`settle-group-payout-${group.key}`}>
            {method === "wallet" ? <p><strong>Wallet:</strong> <span className="font-mono">{head.payout_wallet_id || "—"}</span></p> : <RevealPayout expense={head} />}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="font-bold text-[#1e3a5f]" data-testid={`settle-group-total-${group.key}`}>{eur(group.total)}</span>
          <button onClick={() => onPay(group.items)} className="px-2.5 py-1.5 rounded-lg bg-green-500 text-white text-xs font-bold" data-testid={`settle-group-pay-${group.key}`}>
            {group.items.length > 1 ? "Mark all paid" : "Mark paid"}
          </button>
        </div>
      </div>
      <button onClick={() => setOpen(!open)} className="w-full px-3 py-1.5 text-xs text-slate-500 flex items-center justify-center gap-1 border-t bg-slate-50" data-testid={`settle-group-toggle-${group.key}`}>
        {open ? "Hide lines" : "Show lines"} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && group.items.map(e => <Line key={e.id} e={e} onPay={onPay} />)}
    </div>
  );
}
